import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyMagicLink, registerPasskey, supportsWebAuthn } from '../auth';
import { useToast } from '../lib/toast';

export default function MagicVerifyPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { push } = useToast();
  const token = params.get('t') || '';

  const [state, setState] = useState<'verifying' | 'ready' | 'registering' | 'error'>('verifying');
  const [email, setEmail] = useState('');
  const [bootstrap, setBootstrap] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setErr('No token'); setState('error'); return; }
    (async () => {
      try {
        const r = await verifyMagicLink(token);
        setEmail(r.email);
        setBootstrap(r.bootstrapToken);
        setDeviceName(guessDeviceName());
        setState('ready');
      } catch (e) { setErr((e as Error).message); setState('error'); }
    })();
  }, [token]);

  async function register() {
    setState('registering');
    setErr(null);
    try {
      await registerPasskey(bootstrap, deviceName || 'Unknown device');
      push('Passkey saved. Welcome.', 'success');
      window.location.href = '/dashboard';
    } catch (e) {
      setErr((e as Error).message);
      setState('ready');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-900/40 via-ink-bg to-ink-bg p-4">
      <div className="w-full max-w-md bg-ink-card border border-ink-border p-8 rounded-xl shadow-2xl">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-7 h-7 rounded bg-brand-600 flex items-center justify-center text-white font-bold">O</div>
          <h1 className="text-xl font-bold">OnlyTrade</h1>
        </div>
        <p className="text-sm text-ink-muted mb-6">Register a passkey on this device</p>

        {state === 'verifying' && <div className="text-sm text-ink-muted">Verifying your link…</div>}

        {state === 'error' && (
          <>
            <div className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded p-3 mb-3 break-words">
              {err || 'Link invalid or expired.'}
            </div>
            <button onClick={() => nav('/login')}
                    className="w-full bg-ink-border hover:bg-ink-border/70 text-ink-text rounded py-2 text-sm">
              Back to sign in
            </button>
          </>
        )}

        {(state === 'ready' || state === 'registering') && (
          <>
            {err && <div className="text-sm text-red-400 bg-red-950/50 border border-red-900 rounded p-2 mb-3 break-words">{err}</div>}
            <div className="text-sm mb-4">
              <div className="text-ink-muted text-xs mb-0.5">Signing in as</div>
              <div className="font-medium">{email}</div>
            </div>
            <label className="block text-xs text-ink-muted mb-1 font-medium">Device name</label>
            <input value={deviceName} onChange={e => setDeviceName(e.target.value)}
                   placeholder="e.g. Daniel iPhone / MacBook Pro"
                   className="w-full px-3 py-2 mb-4 bg-ink-bg border border-ink-border text-ink-text rounded text-sm focus:outline-none focus:border-brand-600" />
            <button onClick={register} disabled={state === 'registering' || !supportsWebAuthn()}
                    className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded py-2.5 text-sm font-semibold disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
              {state === 'registering' ? 'Follow the Face ID / Touch ID prompt…' : (<><span>🔐</span> Register passkey with Face ID</>)}
            </button>
            {!supportsWebAuthn() && (
              <div className="text-xs text-red-400 mt-3">This browser doesn't support passkeys. Try Safari or Chrome on a modern device.</div>
            )}
            <p className="text-[11px] text-ink-muted/70 mt-4 leading-relaxed">
              Your passkey is stored on this device only. It syncs via iCloud Keychain (Apple) or Google Password Manager so you can sign in on other devices in the same ecosystem.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function guessDeviceName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Android/.test(ua)) return 'Android';
  return 'This device';
}
